// L'app dev: un indirizzo solo, da installare una volta sul telefono, che
// apre master sui dati di prova e da cui si sceglie la PR da provare. Chi
// sceglie è `middleware.js`, alla radice del repository: legge il cookie
// e gira ogni richiesta alla preview di Vercel di quella PR. L'app, da
// parte sua, legge lo stesso cookie per sapere dov'è (la pastiglia in alto)
// e chiede l'elenco delle versioni per il foglio da cui si cambia.
//
// Qui sta quello che serve a tutti e due, senza dipendenze: il middleware
// gira su Vercel, fuori dalla build dell'app.

// "master", oppure il numero della PR. Senza cookie non si è nell'app dev.
export const DEV_COOKIE = "incampo-dev"
export const MASTER = "master"

// Gli indirizzi di servizio cominciano per `/_`: il service worker li lascia
// passare alla rete (src/service-worker.js), quindi arrivano al middleware
// anche da un'app che ha già tutto in cache.
export const VERSIONS_PATH = "/_dev/versioni"
const SWITCH_PREFIX = "/_dev/versione/"
export const switchPath = id => `${SWITCH_PREFIX}${id}`

export const isVersionID = value => value === MASTER || /^[1-9]\d{0,5}$/.test(value ?? "")

// "/_dev/versione/183" → "183"; null se l'indirizzo non è quello o il resto
// non è una versione
export const switchTarget = pathname => {
	if (!pathname.startsWith(SWITCH_PREFIX)) return null
	const id = decodeURIComponent(pathname.slice(SWITCH_PREFIX.length)).replace(/\/$/, "")
	return isVersionID(id) ? id : null
}

export const readDevCookie = cookieHeader => {
	for (const part of (cookieHeader ?? "").split(";")) {
		const [name, ...rest] = part.trim().split("=")
		if (name !== DEV_COOKIE) continue
		const value = rest.join("=")
		return isVersionID(value) ? value : null
	}
	return null
}

export const devCookie = id =>
	`${DEV_COOKIE}=${id}; Path=/; Max-Age=31536000; SameSite=Lax; Secure`

// ── LE VERSIONI ────────────────────────────────────────────────────
// Una per PR aperta, con la preview più recente che Vercel ha finito di
// costruire. Le PR arrivano da GitHub, le preview da Vercel, e si legano
// per nome del branch.

const READY = "READY"
const BUILDING = new Set(["BUILDING", "QUEUED", "INITIALIZING"])

// `prs` come li dà GitHub (GET /repos/.../pulls), `deployments` come li dà
// Vercel (GET /v6/deployments)
export const buildVersions = (prs, deployments) => {
	const byBranch = new Map()
	for (const d of deployments) {
		if (d.target === "production") continue
		const branch = d.meta?.githubCommitRef
		if (!branch) continue
		if (!byBranch.has(branch)) byBranch.set(branch, [])
		byBranch.get(branch).push(d)
	}

	const versions = prs.map(pr => {
		const own = (byBranch.get(pr.head.ref) ?? []).sort((a, b) => b.created - a.created)
		const ready = own.find(d => (d.state ?? d.readyState) === READY)
		const newest = own[0]
		const building =
			newest && BUILDING.has(newest.state ?? newest.readyState) ? newest : null
		return {
			pr: pr.number,
			title: pr.title,
			branch: pr.head.ref,
			url: ready ? ready.url : null,
			readyAt: ready ? ready.created : null,
			// una preview nuova in arrivo, dopo quella pronta (o al posto suo)
			buildingSince: building && (!ready || building.created > ready.created) ? building.created : null,
		}
	})

	const lastMove = v => Math.max(v.readyAt ?? 0, v.buildingSince ?? 0)
	return versions.sort((a, b) => lastMove(b) - lastMove(a) || b.pr - a.pr)
}

// ── COME SI LEGGONO ────────────────────────────────────────────────

// "2 ore fa" → "2 ore", per dire "in costruzione da 2 ore"
const withoutAgo = relative => relative.replace(/ fa$/, "")

// La riga sotto il titolo di una PR, nel foglio. `relative` trasforma un
// istante in "2 ore fa" (luxon, nell'app): passato da fuori perché qui non
// ci sono dipendenze.
export const versionSubtitle = (version, relative) => {
	const parts = [`#${version.pr}`]
	if (version.url) {
		parts.push(`aggiornata ${relative(version.readyAt)}`)
		if (version.buildingSince) parts.push("ne arriva una nuova")
	} else if (version.buildingSince) {
		parts.push(`in costruzione da ${withoutAgo(relative(version.buildingSince))}`)
	} else {
		parts.push("nessuna preview pronta")
	}
	return parts.join(" · ")
}
